<template>
    <div data-testid="freelancer-signup-main-page" class="freelancer-signup-step-main-page">
        <VJoyFunnel :profile="funnelProfile" :hide-shapes="shouldHideShapes" layout-width="medium">
            <template #funnel-header>
                <VJoyFunnelHeader>
                    <template #funnel-header-left>
                        <MaltImg v-if="funnelProfile === 'default'" src="/navbar/logos/malt-logo-red.svg" alt="Malt" height="40px" />
                        <MaltImg v-else src="/navbar/logos/malt-strategy.svg" alt="Malt" height="40px" />
                    </template>
                </VJoyFunnelHeader>
            </template>
            <template #funnel-stepper>
                <VJoyStepper
                    id="profile-freelancer-signup-stepper"
                    :step="currentMainStep"
                    :sub-step="currentSubStep"
                    data-testid="freelancer-signup-stepper"
                >
                    <VJoyStep
                        v-for="(mainStep, index) in stepGroups"
                        :id="`profile-freelancer-signup-stepper-step--${index}--${mainStep}`"
                        :key="`profile-freelancer-signup-stepper-step--${index}--${mainStep}`"
                        :data-testid="`profile-freelancer-signup-stepper-step--${index}--${mainStep}`"
                        :sub-steps="mainStep.nbSubStep"
                    >
                        {{ labelOf(mainStep.group) }}
                    </VJoyStep>
                </VJoyStepper>
            </template>
            <template #default>
                <NuxtPage :transition="subPageSlideTransitionProps" :is-transition-finished="isTransitionDone" />
                <FooterAdminTools />
            </template>
        </VJoyFunnel>
        <LazyAmplitudeSessionReplayStarter v-if="$ff('amplitude-recording-for-freelancer-signup')" />
    </div>
</template>
<script setup lang="ts">
    import {computed, onMounted, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {FooterAdminTools, LazyAmplitudeSessionReplayStarter, MaltImg} from '#components';
    import {callOnce, definePageMeta, useLogger, useTranslation} from '#imports';
    import {usePageSlideTransition} from '@malt/utils';
    import {pushVJoySnackbar, VJoyFunnel, VJoyFunnelHeader, VJoyStep, VJoyStepper} from '@maltjoy/core-vue';
    import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
    import {useFreelancerSignupNavigation} from '~/features/freelancer-signup/navigation/composables/useFreelancerSignupNavigation';
    import {useFreelancerSignupNavigationStore} from '~/features/freelancer-signup/navigation/store/FreelancerSignupNavigationStore';
    import {
        type FreelancerSignupGroupStep,
        FreelancerSignupStepGroup,
        type FreelancerSignupStepRoute,
    } from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
    import {useFreelancerSignupTracking} from '~/features/freelancer-signup/track/composables/useFreelancerSignupTracking';
    import {useFreelancerSignupFunctionalError} from '~/features/freelancer-signup/workflow/composables/useFreelancerSignupFunctionalError';
    import {useFreelancerSignupStore} from '~/features/freelancer-signup/workflow/store/useFreelancerSignupStore';
    import {useIndustryExpertiseStore} from '~/features/industry-expertise/store/industryExpertiseStore';
    import {useRulesStore} from '~/store/rules/rulesStore';

    definePageMeta({
        layout: false,
    });

    // Stores
    const freelancerSignupStore = useFreelancerSignupStore();
    const {workflow} = storeToRefs(freelancerSignupStore);
    const {funnelContext, allRoutes, currentStep, stepGroups} = storeToRefs(useFreelancerSignupNavigationStore());
    const rulesStore = useRulesStore();
    const industryExpertiseStore = useIndustryExpertiseStore();

    // Refs
    const isTransitionDone = ref(true);

    // Composable
    const logger = useLogger();
    const {t} = useTranslation();
    const track = useFreelancerSignupTracking();
    const freelancerSignupNavigation = useFreelancerSignupNavigation();
    const {isErrorRequiringToRedirect, isErrorWithFunctionalErrorCode, askForRedirectionIfNeeded} = useFreelancerSignupFunctionalError();
    const subPageSlideTransitionProps = usePageSlideTransition({
        basePath: '/profile/freelancer-signup/',
        pathsOrder: [...(allRoutes.value as FreelancerSignupStepRoute[]).map((route) => route.replace('/profile/freelancer-signup/', ''))],
        animationType: 'page-slide',
        onBeforeEnter: () => {
            isTransitionDone.value = false;
        },
        onAfterEnter: () => {
            isTransitionDone.value = true;
        },
    });

    await callOnce('freelancer-signup-main', async () => {
        try {
            await freelancerSignupStore.getMaybeWorkflow();
            await freelancerSignupNavigation.initializeUsingWorkflow();

            const context = workflow.value?.context || funnelContext.value;
            const vertical = context === FreelancerSignupContext.STRATEGY ? 'STRATEGY' : 'FREELANCER';

            const allPromises: Promise<unknown>[] = [rulesStore.init({vertical})];
            if (context === FreelancerSignupContext.STRATEGY) {
                allPromises.push(industryExpertiseStore.loadIndustryExpertises());
            }

            await Promise.all(allPromises);
        } catch (error: unknown) {
            if (isErrorWithFunctionalErrorCode(error) && isErrorRequiringToRedirect(error)) {
                await askForRedirectionIfNeeded(error);
            } else {
                logger.error(error);
                pushVJoySnackbar({
                    props: {
                        message: t('profile.creation.common.notification.error.technical'),
                        level: 'error',
                    },
                });
            }
        }
        try {
            const context = workflow.value?.context || funnelContext.value;
            const vertical = context === FreelancerSignupContext.STRATEGY ? 'STRATEGY' : 'FREELANCER';
            await rulesStore.init({vertical});
        } catch (error: unknown) {
            logger.error('Error on rules store initialization', error);
        }
    });

    // Computed
    const shouldHideShapes = computed((): boolean => currentStep.value.hideShapes);
    const funnelProfile = computed((): 'default' | 'strategy' => {
        switch (funnelContext.value) {
            case FreelancerSignupContext.STRATEGY:
                return 'strategy';
            case FreelancerSignupContext.OPEN:
            case FreelancerSignupContext.REGULAR:
            default:
                return 'default';
        }
    });
    const currentMainStep = computed(
        () => (stepGroups.value as FreelancerSignupGroupStep[]).find((stepGroup) => stepGroup.group === currentStep.value.group)!.groupPosition,
    );
    const currentSubStep = computed((): number => currentStep.value.subStep);

    // Hooks
    onMounted(() => {
        if (!workflow.value?.id) {
            track.creationStarted();
        }
    });

    function labelOf(group: FreelancerSignupStepGroup): string {
        switch (group) {
            case FreelancerSignupStepGroup.INTRODUCE_YOURSELF:
                return t('profile.signup.stepper.group.introduce');
            case FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT:
                return t('profile.signup.stepper.group.talents');
            case FreelancerSignupStepGroup.SET_PREFERENCES:
                return t('profile.signup.stepper.group.preferences');
            case FreelancerSignupStepGroup.READY_TO_GO:
                return t('profile.signup.stepper.group.ready');
        }
    }
</script>
<style lang="scss">
    .freelancer-signup-step-main-page {
        .cta--hidden {
            visibility: hidden;
        }
    }
</style>
